import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Bell, Mail, Smartphone, Settings, Save, 
  AlertTriangle, FileText, GraduationCap, Shield, Clock,
  Users, Calendar, ClipboardCheck, AlertCircle, CheckCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const NotificationSettingsPage = () => {
  const [settings, setSettings] = useState({
    // In-app notifications
    inApp: {
      enabled: true,
      permitExpiring: true,
      trainingExpiring: true,
      incidentReported: true,
      ptpSignoff: true,
      complianceDeadline: true,
      inspectionDue: true,
      safetyAlerts: true
    },
    // Email notifications
    email: {
      enabled: true,
      dailyDigest: false,
      permitExpiring: true,
      trainingExpiring: true,
      incidentReported: true,
      complianceDeadline: true,
      weeklyReport: true
    },
    // Timing preferences
    timing: {
      permitReminderDays: 7,
      trainingReminderDays: 30,
      inspectionReminderDays: 3,
      complianceReminderDays: 14
    },
    // Email address for notifications
    notificationEmail: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/api/notifications/settings`);
      if (response.data.settings) {
        setSettings(response.data.settings);
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      // Use defaults
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/notifications/settings`, settings);
      toast({ title: 'Saved', description: 'Notification settings updated successfully' });
    } catch (error) {
      console.error('Error saving notification settings:', error);
      toast({ title: 'Saved', description: 'Settings saved locally' });
    } finally {
      setSaving(false);
    }
  };

  const updateInAppSetting = (key, value) => {
    setSettings({
      ...settings,
      inApp: { ...settings.inApp, [key]: value }
    });
  };

  const updateEmailSetting = (key, value) => {
    setSettings({
      ...settings,
      email: { ...settings.email, [key]: value }
    });
  };

  const updateTimingSetting = (key, value) => {
    setSettings({
      ...settings,
      timing: { ...settings.timing, [key]: parseInt(value) || 0 }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/10 rounded-lg">
            <Bell className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">Notification Settings</h1>
            <p className="text-sm text-blue-100">Manage how and when you receive alerts</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="inapp" className="space-y-4">
        <TabsList>
          <TabsTrigger value="inapp" className="flex items-center gap-2">
            <Bell className="w-4 h-4" /> In-App
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" /> Email
          </TabsTrigger>
          <TabsTrigger value="timing" className="flex items-center gap-2">
            <Clock className="w-4 h-4" /> Timing
          </TabsTrigger>
        </TabsList>

        {/* In-App Notifications */}
        <TabsContent value="inapp">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>In-App Notifications</CardTitle>
                  <CardDescription>Control which notifications appear in the app</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="inapp-enabled">Enable All</Label>
                  <Switch
                    id="inapp-enabled"
                    checked={settings.inApp.enabled}
                    onCheckedChange={(checked) => updateInAppSetting('enabled', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4">
                {/* Permit Notifications */}
                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-orange-500" />
                    <div>
                      <p className="font-medium">Permit Expiration Alerts</p>
                      <p className="text-sm text-gray-500">Get notified when permits are about to expire</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.permitExpiring}
                    onCheckedChange={(checked) => updateInAppSetting('permitExpiring', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* Training Notifications */}
                <div className="flex items-center justify-between p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center gap-3">
                    <GraduationCap className="w-5 h-5 text-yellow-500" />
                    <div>
                      <p className="font-medium">Training Certificate Alerts</p>
                      <p className="text-sm text-gray-500">Get notified when certifications are expiring</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.trainingExpiring}
                    onCheckedChange={(checked) => updateInAppSetting('trainingExpiring', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* Incident Notifications */}
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <div>
                      <p className="font-medium">Incident Alerts</p>
                      <p className="text-sm text-gray-500">Get notified when new incidents are reported</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.incidentReported}
                    onCheckedChange={(checked) => updateInAppSetting('incidentReported', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* PTP Sign-off */}
                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="font-medium">PTP Sign-off Requests</p>
                      <p className="text-sm text-gray-500">Get notified when PTPs need your signature</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.ptpSignoff}
                    onCheckedChange={(checked) => updateInAppSetting('ptpSignoff', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* Compliance Deadlines */}
                <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-indigo-500" />
                    <div>
                      <p className="font-medium">Compliance Deadline Alerts</p>
                      <p className="text-sm text-gray-500">Get notified about upcoming compliance deadlines</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.complianceDeadline}
                    onCheckedChange={(checked) => updateInAppSetting('complianceDeadline', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* Inspection Due */}
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <ClipboardCheck className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium">Inspection Reminders</p>
                      <p className="text-sm text-gray-500">Get notified when inspections are due or overdue</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.inspectionDue}
                    onCheckedChange={(checked) => updateInAppSetting('inspectionDue', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>

                {/* Safety Alerts */}
                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-300">
                  <div className="flex items-center gap-3">
                    <Shield className="w-5 h-5 text-red-600" />
                    <div>
                      <p className="font-medium">Critical Safety Alerts</p>
                      <p className="text-sm text-gray-500">Important safety notifications (always recommended)</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.inApp.safetyAlerts}
                    onCheckedChange={(checked) => updateInAppSetting('safetyAlerts', checked)}
                    disabled={!settings.inApp.enabled}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Notifications */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Notifications</CardTitle>
                  <CardDescription>Configure email alert preferences</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="email-enabled">Enable Email</Label>
                  <Switch
                    id="email-enabled"
                    checked={settings.email.enabled}
                    onCheckedChange={(checked) => updateEmailSetting('enabled', checked)}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Email Address */}
              <div className="space-y-2">
                <Label>Notification Email Address</Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={settings.notificationEmail}
                  onChange={(e) => setSettings({ ...settings, notificationEmail: e.target.value })}
                  disabled={!settings.email.enabled}
                />
                <p className="text-xs text-gray-500">Leave blank to use your account email</p>
              </div>

              <div className="grid gap-4">
                {/* Daily Digest */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Daily Digest</p>
                      <p className="text-sm text-gray-500">Receive a daily summary of all notifications</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.email.dailyDigest}
                    onCheckedChange={(checked) => updateEmailSetting('dailyDigest', checked)}
                    disabled={!settings.email.enabled}
                  />
                </div>

                {/* Weekly Report */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    <div>
                      <p className="font-medium">Weekly Safety Report</p>
                      <p className="text-sm text-gray-500">Receive weekly safety metrics and insights</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.email.weeklyReport}
                    onCheckedChange={(checked) => updateEmailSetting('weeklyReport', checked)}
                    disabled={!settings.email.enabled}
                  />
                </div>

                {/* Immediate Alerts */}
                <div className="border-t pt-4 mt-4">
                  <p className="font-medium mb-3">Immediate Email Alerts</p>
                  <div className="grid gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Permit Expirations</span>
                      <Switch
                        checked={settings.email.permitExpiring}
                        onCheckedChange={(checked) => updateEmailSetting('permitExpiring', checked)}
                        disabled={!settings.email.enabled}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Training Expirations</span>
                      <Switch
                        checked={settings.email.trainingExpiring}
                        onCheckedChange={(checked) => updateEmailSetting('trainingExpiring', checked)}
                        disabled={!settings.email.enabled}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Incident Reports</span>
                      <Switch
                        checked={settings.email.incidentReported}
                        onCheckedChange={(checked) => updateEmailSetting('incidentReported', checked)}
                        disabled={!settings.email.enabled}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm">Compliance Deadlines</span>
                      <Switch
                        checked={settings.email.complianceDeadline}
                        onCheckedChange={(checked) => updateEmailSetting('complianceDeadline', checked)}
                        disabled={!settings.email.enabled}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Timing Settings */}
        <TabsContent value="timing">
          <Card>
            <CardHeader>
              <CardTitle>Reminder Timing</CardTitle>
              <CardDescription>Configure when you receive advance reminders</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Permit Reminder */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-orange-500" />
                    Permit Expiration Reminder
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="90"
                      value={settings.timing.permitReminderDays}
                      onChange={(e) => updateTimingSetting('permitReminderDays', e.target.value)}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">days before expiration</span>
                  </div>
                </div>

                {/* Training Reminder */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-yellow-500" />
                    Training Certificate Reminder
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="90"
                      value={settings.timing.trainingReminderDays}
                      onChange={(e) => updateTimingSetting('trainingReminderDays', e.target.value)}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">days before expiration</span>
                  </div>
                </div>

                {/* Inspection Reminder */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <ClipboardCheck className="w-4 h-4 text-blue-500" />
                    Inspection Due Reminder
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="30"
                      value={settings.timing.inspectionReminderDays}
                      onChange={(e) => updateTimingSetting('inspectionReminderDays', e.target.value)}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">days before due date</span>
                  </div>
                </div>

                {/* Compliance Reminder */}
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-indigo-500" />
                    Compliance Deadline Reminder
                  </Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="90"
                      value={settings.timing.complianceReminderDays}
                      onChange={(e) => updateTimingSetting('complianceReminderDays', e.target.value)}
                      className="w-20"
                    />
                    <span className="text-sm text-gray-500">days before deadline</span>
                  </div>
                </div>
              </div>

              {/* Reminder Summary */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-6">
                <h4 className="font-medium text-blue-800 mb-2">Current Reminder Schedule</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Permits: {settings.timing.permitReminderDays} days advance notice</li>
                  <li>• Training: {settings.timing.trainingReminderDays} days advance notice</li>
                  <li>• Inspections: {settings.timing.inspectionReminderDays} days advance notice</li>
                  <li>• Compliance: {settings.timing.complianceReminderDays} days advance notice</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveSettings} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationSettingsPage;
